import { useState, useEffect, useCallback } from 'react';
import { commentApi } from '../../utils/studyApiService';
import './CommentSection.css';

const CommentSection = ({ studyId, entryId }) => {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [newContent, setNewContent] = useState('');
  const [replyTo, setReplyTo] = useState(null); // { id, content }
  const [replyContent, setReplyContent] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await commentApi.list(studyId, entryId);
      if (res.success) {
        setComments(res.data.items || []);
        setTotal(res.data.pagination?.total || 0);
      }
    } catch (e) {
      setError('댓글을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [studyId, entryId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newContent.trim()) return;
    setSubmitting(true);
    try {
      await commentApi.create(studyId, entryId, { content: newContent, parent_id: null });
      setNewContent('');
      await fetchComments();
    } catch (e) {
      setError('댓글 작성에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplySubmit = async (e, parentId) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    setSubmitting(true);
    try {
      await commentApi.create(studyId, entryId, { content: replyContent, parent_id: parentId });
      setReplyTo(null);
      setReplyContent('');
      await fetchComments();
    } catch (e) {
      setError('대댓글 작성에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (e, commentId) => {
    e.preventDefault();
    if (!editContent.trim()) return;
    setSubmitting(true);
    try {
      await commentApi.update(studyId, entryId, commentId, { content: editContent });
      setEditingId(null);
      setEditContent('');
      await fetchComments();
    } catch (e) {
      setError('댓글 수정에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    try {
      await commentApi.delete(studyId, entryId, commentId);
      await fetchComments();
    } catch (e) {
      setError('댓글 삭제에 실패했습니다.');
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div className="comment-section">
      <h3 className="comment-section-title">💬 댓글 {total}</h3>

      {error && <div className="comment-error">{error}</div>}

      {/* 새 댓글 작성 */}
      <form className="comment-form" onSubmit={handleSubmit}>
        <textarea
          className="comment-textarea"
          placeholder="댓글을 입력하세요..."
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          rows={2}
        />
        <button
          type="submit"
          className="comment-submit-btn"
          disabled={submitting || !newContent.trim()}
        >
          등록
        </button>
      </form>

      {/* 댓글 목록 */}
      {loading ? (
        <div className="comment-loading">불러오는 중...</div>
      ) : (
        <div className="comment-list">
          {comments.map((comment) => (
            <div key={comment.id} className="comment-item">
              {/* 댓글 본문 */}
              <div className="comment-body">
                <div className="comment-time">{formatTime(comment.created_at)}</div>
                {editingId === comment.id ? (
                  <form className="comment-edit-form" onSubmit={(e) => handleEditSubmit(e, comment.id)}>
                    <textarea
                      className="comment-textarea"
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={2}
                      autoFocus
                    />
                    <div className="comment-edit-actions">
                      <button type="submit" className="comment-btn comment-btn-save" disabled={submitting}>저장</button>
                      <button type="button" className="comment-btn comment-btn-cancel" onClick={() => setEditingId(null)}>취소</button>
                    </div>
                  </form>
                ) : (
                  <>
                    <p className="comment-content">{comment.content}</p>
                    <div className="comment-actions">
                      <button
                        type="button"
                        className="comment-action-btn"
                        onClick={() => { setReplyTo(comment); setReplyContent(''); }}
                      >
                        답글
                      </button>
                      <button
                        type="button"
                        className="comment-action-btn"
                        onClick={() => { setEditingId(comment.id); setEditContent(comment.content); }}
                      >
                        수정
                      </button>
                      <button
                        type="button"
                        className="comment-action-btn comment-action-delete"
                        onClick={() => handleDelete(comment.id)}
                      >
                        삭제
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* 대댓글 입력 */}
              {replyTo?.id === comment.id && (
                <form
                  className="reply-form"
                  onSubmit={(e) => handleReplySubmit(e, comment.id)}
                >
                  <textarea
                    className="comment-textarea"
                    placeholder="답글을 입력하세요..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    rows={2}
                    autoFocus
                  />
                  <div className="comment-edit-actions">
                    <button type="submit" className="comment-btn comment-btn-save" disabled={submitting || !replyContent.trim()}>등록</button>
                    <button type="button" className="comment-btn comment-btn-cancel" onClick={() => setReplyTo(null)}>취소</button>
                  </div>
                </form>
              )}

              {/* 대댓글 목록 */}
              {comment.replies?.length > 0 && (
                <div className="reply-list">
                  {comment.replies.map((reply) => (
                    <div key={reply.id} className="reply-item">
                      <div className="comment-time">{formatTime(reply.created_at)}</div>
                      {editingId === reply.id ? (
                        <form className="comment-edit-form" onSubmit={(e) => handleEditSubmit(e, reply.id)}>
                          <textarea
                            className="comment-textarea"
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            rows={2}
                            autoFocus
                          />
                          <div className="comment-edit-actions">
                            <button type="submit" className="comment-btn comment-btn-save" disabled={submitting}>저장</button>
                            <button type="button" className="comment-btn comment-btn-cancel" onClick={() => setEditingId(null)}>취소</button>
                          </div>
                        </form>
                      ) : (
                        <>
                          <p className="comment-content">{reply.content}</p>
                          <div className="comment-actions">
                            <button
                              type="button"
                              className="comment-action-btn"
                              onClick={() => { setEditingId(reply.id); setEditContent(reply.content); }}
                            >
                              수정
                            </button>
                            <button
                              type="button"
                              className="comment-action-btn comment-action-delete"
                              onClick={() => handleDelete(reply.id)}
                            >
                              삭제
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentSection;
